// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model, read https://goo.gl/KwvDNy.
// This link also includes instructions on opting out of this behavior.

const colourSchemeMap = {
  elastik: {
    bannerBackgroundColour: "#ffff6e",
    bannerFontColour: "#0000ff",
    buttonBackgroundColour: "#0000ff",
    buttonFontColour: "#ffffff",
  },
  edcompanion: {
    bannerBackgroundColour: "#5fbf7f",
    bannerFontColour: "#ffffff",
    buttonBackgroundColour: "#3f4675",
    buttonFontColour: "#ffffff",
  },
};

const colourSchemeName = /elastik/.test(window.location.hostname)
  ? "elastik"
  : "edcompanion";
const colourScheme = colourSchemeMap[colourSchemeName];

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/.exec(window.location.hostname)
);

// The register() method of the ServiceWorkerContainer interface creates or updates a
// ServiceWorkerRegistration.
//
// This implementation just forwards the call to the registerValidSW() function below.
export function register(config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebook/create-react-app/issues/2374
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        // This is running on localhost. Let's check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config);

        // Add some additional logging to localhost, pointing developers to the
        // service worker/PWA documentation.
        navigator.serviceWorker.ready.then(() => {
          console.log(
            'This web app is being served cache-first by a service ' +
              'worker. To learn more, visit https://cra.link/PWA'
          );
        });
      } else {
        // Is not localhost. Just register service worker
        registerValidSW(swUrl, config);
      }
    });
  }
}

// If successful, a service worker registration ties the provided script URL to a scope,
// which is subsequently used for navigation matching. You can call this method unconditionally
// from the controlled page. I.e., you don't need to first check whether there's an active registration.
//
function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        // skipcq: JS-0050
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // At this point, the old content will have been purged and
              // the fresh content will have been added to the cache.
              // It's the perfect time to display a "New content is
              // available; please refresh." message in your web app.

              // display a countdown timer banner with a button
              let countdownIntervalRef = null, // skipcq: JS-0242 (Use const instead of let.)
                minsRemaining = 5, // set countdown timer initially to 5 mins 0 seconds
                secsRemaining = 0;
              window.handleServiceWorkerNotificationOnClick = () => {
                window.clearInterval(countdownIntervalRef);
                document.location.reload();
                if (config.onUpdate) {
                  config.onUpdate(registration);
                }
              };

              window.handleServiceWorkerTimeoutExpired = () => {
                window.clearInterval(countdownIntervalRef);
                // skipcq: JS-0052
                alert(
                  "New update is available, click Ok to reload the application"
                );
                document.location.reload(true);
                // Execute callback
                if (config.onUpdate) {
                  config.onUpdate(registration);
                }
              };

              const fakeAlert = document.createElement("div");
              fakeAlert.setAttribute(
                "style",
                `background-color: ${colourScheme.bannerBackgroundColour};
                 color: ${colourScheme.bannerFontColour};
                 position: absolute;
                 top: 0;
                 left: 0;
                 width: 100%;
                 height: 56px;
                 text-align: center;
                 font-size: 2em;
                 z-index:9999;`
              );
              fakeAlert.innerHTML = `New update is available, reloading
                <span id="reloadCountdown">in ${minsRemaining}m ${secsRemaining}sec</span>
                <button onclick="window.handleServiceWorkerNotificationOnClick();"
                        style="
                          font-size: 0.5em;
                          border-radius: 2em;
                          background-color: ${colourScheme.buttonBackgroundColour};
                          color: ${colourScheme.buttonFontColour};
                          border-style: none;
                          padding: 0.5em;
                          vertical-align: middle;
                        "
                        >Reload Now
                </button>`;
              document.body.appendChild(fakeAlert);
              countdownIntervalRef = window.setInterval(() => {
                const totalSecsRemaining =
                  minsRemaining * 60 + secsRemaining - 1; // countdown by 1 sec
                if (totalSecsRemaining > 0) {
                  minsRemaining = Math.floor(totalSecsRemaining / 60);
                  secsRemaining = totalSecsRemaining % 60;
                  document.getElementById(
                    "reloadCountdown"
                  ).innerHTML = `in ${minsRemaining}m ${secsRemaining}sec`;
                } else {
                  // countdown is finished. reload.
                  window.handleServiceWorkerTimeoutExpired();
                }
              }, 1000);

              // Execute callback
              // skipcq: JS-W1044
              if (config?.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // At this point, everything has been precached.
              // It's the perfect time to display a
              // "Content is cached for offline use." message.
              console.log('Content is cached for offline use.');

              // Execute callback
              // skipcq: JS-W1044
              if (config?.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

function checkValidServiceWorker(swUrl, config) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        // skipcq: JS-0050
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.');
    });
}

// Unregisters the service worker registration and returns a Promise.
// The service worker will finish any ongoing operations before it is unregistered.
export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}
